/**
 * Phase 17 W1 — Token bridge for Pixi consumers.
 *
 * CSS custom properties cannot be read from <canvas>; Pixi needs hex strings
 * or 0xRRGGBB integers. This module re-exports the canonical token values
 * declared in globals.css / theme.css so Pixi layers don't ship hex literals.
 *
 * Atlas-parity test (W5) enforces that every value here matches the CSS-var
 * value of the same name. Drift here OR in globals.css/theme.css fails the test.
 *
 * Source of truth: UI-SPEC §2.1, §2.4, §2.5.
 */

export interface ActionTokens {
  readonly ctaPrimary: string
  readonly ctaPrimaryTop: string
  readonly ctaPrimaryDeep: string
  readonly ctaConfirm: string
  readonly ctaConfirmTop: string
  readonly ctaConfirmDeep: string
  readonly ctaDestructive: string
  readonly ctaDestructiveTop: string
  readonly ctaDestructiveDeep: string
  readonly currencyGem: string
  readonly currencyGemDeep: string
  readonly currencyCoin: string
  readonly textPrimary: string
  readonly textSecondary: string
  readonly textTertiary: string
  readonly textStroke: string
  readonly cubeStroke: string
  readonly inkwell: string
  /** Folk reskin: LIGHT label/catch-light role. textPrimary is now dark ink,
   * so Pixi highlights/jewels/sparks/lit-edges that must read LIGHT use this
   * (mirrors --color-on-action). */
  readonly onAction: string
  /** Folk reskin: warm light halo behind display numerals / glyphs
   * (mirrors --color-text-halo). */
  readonly textHalo: string
}

// Folk-art reskin (DEC-015): VALUES remapped to the warm folk palette; must
// stay byte-aligned with globals.css :root (atlas-parity W5 gate). The semantic
// flip turns text*/inkwell/textStroke dark; textSecondary/Tertiary become hex
// (were rgba) so they are hexToInt-safe for any Pixi consumer.
export const actionTokens: ActionTokens = {
  ctaPrimary: '#f7a920',
  ctaPrimaryTop: '#ffc24d',
  ctaPrimaryDeep: '#d98708',
  ctaConfirm: '#6bae4a',
  ctaConfirmTop: '#82c75f',
  ctaConfirmDeep: '#4e8c33',
  ctaDestructive: '#c73c28',
  ctaDestructiveTop: '#ee5d43',
  ctaDestructiveDeep: '#9b2310',
  currencyGem: '#b14a93',
  currencyGemDeep: '#8e2f72',
  currencyCoin: '#f7a920',
  textPrimary: '#3b2a1c',
  textSecondary: '#6e5a47',
  textTertiary: '#a88e73',
  textStroke: '#3b2a1c',
  cubeStroke: '#6e5a47',
  inkwell: '#3b2a1c',
  onAction: '#ffffff',
  textHalo: '#fff8ec',
} as const

export interface ThemeTokenRecord {
  readonly bgDeep: string
  readonly bgMid: string
  readonly bgHighlight: string
  readonly surfaceDeep: string
  readonly surfaceMid: string
  readonly surfaceLight: string
  readonly surfaceStroke: string
  readonly magic: string
  readonly magicDeep: string
  readonly playerA: string
  readonly playerAGlow: string
  readonly playerB: string
  readonly playerBGlow: string
  readonly cubeFace: string
  readonly cubeSide: string
}

// WS0b (ARCA-87) theme collapse: the 4-theme architecture is gone — there is a
// single, static take-two (folk-art) identity-token set. Pixi/QR consumers read
// these values directly (no more runtime theme swap / descriptor css_vars
// lookup). Mirrors the single :root identity block in theme.css (atlas-parity
// gate). Source: "Docs/UI-prototype take two" (DEC-015).
export const themeTokens: ThemeTokenRecord = {
  bgDeep: '#f7e4be',
  bgMid: '#fcefd6',
  bgHighlight: '#ffe6bf',
  surfaceDeep: '#f7e4be',
  surfaceMid: '#fcefd6',
  surfaceLight: '#fffdf7',
  surfaceStroke: '#c9b48c',
  magic: '#7b6bdd',
  magicDeep: '#5847c4',
  playerA: '#ee5d43',
  playerAGlow: '#ff7152',
  playerB: '#2ba39c',
  playerBGlow: '#34bdb4',
  cubeFace: '#fbefd3',
  cubeSide: '#6e5a47',
}

export const spacing = {
  1: '4px',
  2: '8px',
  3: '12px',
  4: '16px',
  5: '24px',
  6: '32px',
} as const

export const radii = {
  sm: '4px',
  md: '6px',
  lg: '8px',
  xl: '10px',
  '2xl': '12px',
  '3xl': '14px',
  pill: '999px',
} as const

export const fontSize = {
  9: '9px',
  10: '10px',
  11: '11px',
  12: '12px',
  13: '13px',
  14: '14px',
  16: '16px',
  18: '18px',
  20: '20px',
  22: '22px',
  36: '36px',
  52: '52px',
  100: '100px',
} as const

/**
 * Cosmetic-item placeholder palette (Plan 20-06 META-06). These are inherent
 * cosmetic-item identity colors (amethyst, bronze, emerald, gold …) used as
 * the preview-swatch fill while real cosmetic art remains operator/backlog.
 * Held here so the token-gate (check-token-usage.sh) doesn't trip on inherent
 * content colors that intentionally diverge from theme/UI tokens.
 */
export const cosmeticPalette = {
  amethyst: '#8B5CF6',
  bronze: '#CD7F32',
  emerald: '#50C878',
  silver: '#C0C0C0',
  midnightBlue: '#191970',
  gold: '#FFD700',
  sapphire: '#0F52BA',
  velvetNoir: '#2C1810',
  ruby: '#E0115F',
  copper: '#B87333',
  diamond: '#B9F2FF',
} as const

/**
 * Convert "#RRGGBB" (or "#RGB") to a Pixi-compatible 0xRRGGBB int.
 *
 * Throws if the input is not a hex color literal. The ActionTokens record
 * exposes three rgba() values (textSecondary, textTertiary, color-shadow*)
 * that look passable to this function but would silently produce NaN under
 * the previous lenient implementation — Pixi's Graphics.fill(NaN) then
 * renders black/transparent with no diagnostic. Failing loudly here surfaces
 * misuse at call-time instead of producing invisible art.
 */
export const hexToInt = (hex: string): number => {
  if (typeof hex !== 'string' || !hex.startsWith('#')) {
    throw new Error(
      `hexToInt: expected a hex color string starting with '#', got "${hex}"`,
    )
  }
  const stripped = hex.slice(1)
  if (stripped.length !== 3 && stripped.length !== 6) {
    throw new Error(
      `hexToInt: expected #RGB or #RRGGBB, got "${hex}" (length ${stripped.length})`,
    )
  }
  if (!/^[0-9a-fA-F]+$/.test(stripped)) {
    throw new Error(
      `hexToInt: expected only hex digits after '#', got "${hex}"`,
    )
  }
  // Expand short form (#abc → #aabbcc) before parsing.
  const full =
    stripped.length === 3
      ? stripped
          .split('')
          .map((c) => c + c)
          .join('')
      : stripped
  return parseInt(full, 16)
}
